import type { ProcedureColumn } from "../models/audit/auditSession";

/**
 * Minimal shape shared by `ControlAttribute` and `WorkStepAttribute` — the two
 * attribute domains a column can be mapped to (see `ProcedureColumn.attributeId`
 * / `workStepAttributeId`). Callers keep passing either array as-is; both
 * satisfy this structurally.
 */
export interface AttributeReference {
  id: string;
  reference: string;
}

/**
 * An attribute as offered in the workpaper builder's attribute picker: the
 * mapping shape plus the statement shown beside each option, and the
 * description a drafted test is generated from. Both `ControlAttribute` and
 * `WorkStepAttribute` satisfy this structurally.
 */
export interface AttributeOption extends AttributeReference {
  statement: string;
  description: string;
}

/**
 * The attribute this column is mapped to, whichever domain it comes from. A
 * column sets at most one of `attributeId` (control attribute) / `workStepAttributeId`
 * (work-step attribute) — mirrors the BE `ProcedureColumn.work_step_attribute_id`
 * doc: "Work-step sibling of attribute_id... a column sets at most one of them."
 */
export function effectiveAttributeId(
  column: ProcedureColumn,
): string | null | undefined {
  return column.attributeId ?? column.workStepAttributeId;
}

/**
 * Calculate the ordinal position of an attribute test column among all columns
 * with the same effective attribute id, sorted by columnOrder.
 *
 * @param currentColumn - The column to calculate the position for
 * @param allColumns - All columns across all procedures
 * @returns The 1-indexed ordinal position (e.g., 1, 2, 3)
 */
function calculateAttributeTestOrdinal(
  currentColumn: ProcedureColumn,
  allColumns: ProcedureColumn[],
): number {
  const currentAttributeId = effectiveAttributeId(currentColumn);
  // Filter to attribute test columns with the same effective attribute id
  const sameAttributeColumns = allColumns
    .filter(
      (col) =>
        col.attributeTestColumn &&
        effectiveAttributeId(col) === currentAttributeId,
    )
    // Tie-break on the attribute-test column id, comparing code points. A column is two rows —
    // a procedure_column and an attribute_test_column, each with its own unrelated uuid — and the
    // BE numbers by the attribute_test_column one, so tying on `a.id` here (the procedure_column)
    // would disagree with the export about half the time. `localeCompare` would too: it orders by
    // the browser's language, and Danish, Norwegian and Faroese sort "aa" near the end of the
    // alphabet, where the BE compares byte by byte. Ties are reachable — the drag-to-front handler
    // in WorkPaperTemplate.tsx floors the new order at 1000, colliding with the second column.
    .sort((a, b) => {
      if (a.columnOrder !== b.columnOrder) return a.columnOrder - b.columnOrder;
      const [left, right] = [
        a.attributeTestColumn?.id ?? "",
        b.attributeTestColumn?.id ?? "",
      ];
      return left < right ? -1 : left > right ? 1 : 0;
    });

  // Find the position (1-indexed)
  const position =
    sameAttributeColumns.findIndex((col) => col.id === currentColumn.id) + 1;

  return position;
}

/**
 * Tickmark code prefix for an attribute-test column.
 *
 * Two columns can support the same attribute, so a bare attribute reference
 * ("D") is ambiguous. When an attribute has more than one column we dot-suffix
 * the reference with the column's 1-indexed display-order ordinal ("D.1",
 * "D.2"); a solo column keeps the bare reference ("B"). The separator keeps the
 * ordinal readable when the reference itself ends in a digit — attribute "A1"
 * gives "A1.1"/"A1.2", not "A11"/"A12" — and the dot matches the hierarchical
 * numbering auditors use. Tickmark rows then always append ".{n}" → "D.1.1",
 * "A1.2.1", "B.1". Returns undefined when there's no attribute. Mirrored by
 * the BE `attribute_column_codes`, which stamps the same codes onto the
 * exported workpaper — keep the two in sync.
 */
export function getAttributeTestColumnCode(
  column: ProcedureColumn,
  allColumns: ProcedureColumn[],
  attributes: AttributeReference[],
): string | undefined {
  const attributeId = effectiveAttributeId(column);
  if (!attributeId) return undefined;
  const reference = attributes.find(
    (attr) => attr.id === attributeId,
  )?.reference;
  if (!reference) return undefined;

  const sameAttributeColumns = allColumns.filter(
    (col) =>
      col.attributeTestColumn && effectiveAttributeId(col) === attributeId,
  );
  if (sameAttributeColumns.length <= 1) return reference;
  return `${reference}.${calculateAttributeTestOrdinal(column, allColumns)}`;
}

/**
 * Labelled header for an attribute-test column: "<code> - <name>", e.g.
 * "D.1 - Config roles present".
 *
 * Column names conventionally lead with their attribute's reference
 * ("D - Config roles present"), which the code prefix already names — a leading
 * token the code covers (the bare reference or the code itself) is stripped so
 * the label doesn't double it ("D.1 - D - Config roles present"). Falls back to
 * the plain display name when the column has no code (no attribute mapped).
 *
 * The separator is the one occurring earliest in the name, not the first of the
 * list, so a hyphen-led name still folds when an en-dash appears later
 * ("D - Config – roles"). Which heads count is `namesTheAttribute`.
 */
export function getAttributeTestColumnLabel(
  column: ProcedureColumn,
  allColumns: ProcedureColumn[],
  attributes: AttributeReference[],
): string {
  const name = getColumnDisplayName(column, allColumns, attributes);
  // Undefined for a support/eval column (which can carry an attributeId too, but
  // the code prefix is an attribute-test concept) and for an unmapped attribute;
  // both keep their plain display name.
  const code = column.attributeTestColumn
    ? getAttributeTestColumnCode(column, allColumns, attributes)?.trim()
    : undefined;
  if (!code) return name;
  // An unnamed column's display name is already the generated code shape ("D.1"),
  // so the code alone is the whole label.
  if (!column.attributeTestColumn?.name) return code;

  const reference = attributes
    .find((attr) => attr.id === effectiveAttributeId(column))
    ?.reference?.trim();
  if (namesTheAttribute(name.trim(), code, reference)) return code;

  const separator = [" – ", " — ", " - "]
    .map((candidate) => ({ index: name.indexOf(candidate), candidate }))
    .filter(({ index }) => index !== -1)
    .sort((a, b) => a.index - b.index)[0];
  if (!separator) return `${code} - ${name}`;

  const head = name.slice(0, separator.index).trim();
  const rest = name.slice(separator.index + separator.candidate.length).trim();
  return rest && namesTheAttribute(head, code, reference)
    ? `${code} - ${rest}`
    : `${code} - ${name}`;
}

/**
 * Whether a leading token of a column name is something the code already names:
 * the code itself ("D.2"), the bare attribute reference ("D"), or the reference
 * carrying any ordinal ("D.1" on a column now numbered "D.2" — a stale ordinal
 * left by a rename or a reorder, which would otherwise render as
 * "D.2 - D.1 - Config roles present").
 *
 * The reference is looked up from the attribute rather than recovered from the
 * code by string surgery: a solo column's code is the bare reference with no
 * ordinal to chop, so slicing at the last "." silently ate a character and let
 * an "A1" column swallow the "A" of a *different* attribute. Mirrors the BE
 * `_names_the_attribute`.
 */
function namesTheAttribute(
  token: string,
  code: string,
  reference: string | undefined,
): boolean {
  if (token === code) return true;
  if (!reference) return false;
  if (token === reference) return true;
  const ordinal = token.startsWith(`${reference}.`)
    ? token.slice(reference.length + 1)
    : "";
  return /^\d+$/.test(ordinal);
}

/**
 * Get the display name for a column, handling all column types.
 * For attribute test columns without a custom name, returns the ordinal position
 * in the format "{attributeReference}.{ordinal}" (e.g., "A1.1", "A1.2").
 *
 * @param column - The column to get the display name for
 * @param allColumns - All columns (needed to calculate ordinal position)
 * @param attributes - All attributes the column could be mapped to — control
 *   attributes for a control-based procedure, or work-step attributes for a
 *   work-step-generated one (needed to look up the attribute reference)
 * @returns The display name for the column
 */
export function getColumnDisplayName(
  column: ProcedureColumn,
  allColumns: ProcedureColumn[],
  attributes: AttributeReference[],
): string {
  // Get the base name from the column type
  const baseName =
    column.supportColumn?.name ||
    column.evaluatedColumn?.name ||
    column.attributeTestColumn?.name ||
    column.analyticsColumn?.name ||
    // An analytics column authored before it was named is still identified by the
    // workflow bound to it, which is a real name the author recognises rather
    // than a placeholder.
    column.analyticsColumn?.workflowName;

  // If it's an attribute test column and has no custom name, use ordinal position
  if (column.attributeTestColumn && !column.attributeTestColumn.name) {
    const attributeId = effectiveAttributeId(column);
    if (attributeId) {
      // Find the attribute to get its reference
      const attribute = attributes.find((attr) => attr.id === attributeId);

      if (attribute) {
        const ordinal = calculateAttributeTestOrdinal(column, allColumns);
        return `${attribute.reference}.${ordinal}`;
      }
    }
    // Fallback if attribute not found or no attributeId
    return "Unnamed Attribute Test Column";
  }

  // An analytics column with neither a name nor a bound workflow is named for its
  // kind rather than falling through to the generic placeholder. There is no
  // attribute-reference ordinal here on purpose: the tickmark scheme
  // (`getAttributeTestColumnCode`) covers attribute-test columns only.
  if (column.analyticsColumn && !baseName) {
    return "Unnamed Analytics Column";
  }

  // Return the name or fallback
  return baseName || "Unnamed Column";
}
